import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Input } from '@components/Input';
import { companyService } from '@services/companyService';
import type { InvoiceNumberControl } from '@/types/company';

interface Props {
  companyId: number;
}

const rowKey = (model: number, serie: number) => `${model}-${serie}`;

export const InvoiceNumberControlTab: React.FC<Props> = ({ companyId }) => {
  const [rows, setRows] = useState<InvoiceNumberControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const [newModel, setNewModel] = useState('55');
  const [newSerie, setNewSerie] = useState('1');
  const [newLast, setNewLast] = useState('0');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyService.listInvoiceNumberControls(companyId);
      setRows(data);
      setEdits(Object.fromEntries(data.map((r) => [rowKey(r.model, r.serie), String(r.last_number)])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar numeração');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await companyService.createInvoiceNumberControl(companyId, {
        model: Number(newModel) || 55,
        serie: Number(newSerie) || 0,
        last_number: Number(newLast) || 0,
      });
      setNewSerie('1');
      setNewLast('0');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao adicionar numeração');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (r: InvoiceNumberControl) => {
    setBusy(true);
    setError(null);
    try {
      await companyService.updateInvoiceNumberControl(companyId, {
        model: r.model,
        serie: r.serie,
        last_number: Number(edits[rowKey(r.model, r.serie)]) || 0,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar numeração');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (r: InvoiceNumberControl) => {
    setBusy(true);
    setError(null);
    try {
      await companyService.deleteInvoiceNumberControl(companyId, r.model, r.serie);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao remover numeração');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Numeração de Notas Fiscais"
        subtitle="Controle do último número emitido por modelo/série (usado na emissão direta na SEFAZ). Ajuste o 'Último número' para o valor já autorizado no cutover."
      />
      <CardContent>
        {error && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-slate-200" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">Modelo</th>
                  <th className="py-2 pr-3 font-medium">Série</th>
                  <th className="py-2 pr-3 font-medium">Último número</th>
                  <th className="py-2 pr-3 font-medium">Atualizado em</th>
                  <th className="py-2 pr-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4 text-slate-500">
                      Nenhuma numeração cadastrada. Adicione a série usada na emissão direta.
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const key = rowKey(r.model, r.serie);
                  return (
                    <tr key={key} className="border-b border-slate-50 align-middle">
                      <td className="py-2 pr-3 text-slate-700">{r.model}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.serie}</td>
                      <td className="py-2 pr-3">
                        <input
                          type="number"
                          min={0}
                          value={edits[key] ?? ''}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                          className="w-40 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-3 text-slate-500">
                        {r.updated_at ? new Date(r.updated_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleSave(r)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline mr-4 disabled:opacity-50"
                        >
                          <Save size={14} /> Salvar
                        </button>
                        <button
                          onClick={() => handleDelete(r)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50"
                        >
                          <Trash2 size={14} /> Excluir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Adicionar nova numeração */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-sm font-medium text-slate-700 mb-2">Adicionar numeração</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12 items-end">
            <div className="md:col-span-3">
              <Input label="Modelo" type="number" value={newModel} onChange={(e) => setNewModel(e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <Input label="Série" type="number" value={newSerie} onChange={(e) => setNewSerie(e.target.value)} />
            </div>
            <div className="md:col-span-4">
              <Input label="Último número" type="number" value={newLast} onChange={(e) => setNewLast(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Button onClick={handleAdd} loading={busy} icon={<Plus size={16} />}>
                Adicionar
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Série 0–99, modelo padrão 55 (NF-e). O próximo número emitido será "Último número" + 1.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceNumberControlTab;
